import { ChangeDetectionStrategy, Component, computed, HostListener, inject, Signal, signal, WritableSignal } from "@angular/core";
import { FormControl, FormGroup, NonNullableFormBuilder, ReactiveFormsModule, Validators } from "@angular/forms";
import { faCircleExclamation, faEye, faEyeSlash, faSquareArrowUp, faXmark } from "@fortawesome/pro-light-svg-icons";
import { FaIconLibrary } from "@fortawesome/angular-fontawesome";
import { MatDialogModule } from "@angular/material/dialog";
import { IconModule } from "@ogs/ngx-m3-components";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatButtonModule } from "@angular/material/button";
import { MatInputModule } from "@angular/material/input";
import { AuthService } from "@features/authenticate/services/auth.service";
import { CapsLockNoticeComponent } from "@shared/components/caps-lock-notice/caps-lock-notice.component";
import { NotifyChipComponent } from "@shared/components/notify-chip/notify-chip.component";
import { NotifyChipValueComponent } from "@shared/components/notify-chip/components/notify-chip-value/notify-chip-value.components";
import { SET_PASSWORD_TOKENS } from "@features/authenticate/types/set-password-tokens";
import { passwordMatchValidator, passwordValidator } from "@shared/common/validators.common";
import { MatDividerModule } from "@angular/material/divider";
import { User } from "@features/authenticate/types/user";
import { SetPasswordResponse } from "@features/authenticate/types/set-password-response";
import { TranslationInfo } from "@shared/types/translation-info";
import { provideTranslationScope, TranslationPipeModule } from "@ogs-gmbh/ngx-translate";


@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: "app-core-ui-header-change-password-dialog",
  templateUrl: "./change-password-dialog.component.html",
  styleUrl: "change-password-dialog.component.scss",
  imports: [ MatDialogModule,
    ReactiveFormsModule,
    IconModule,
    MatFormFieldModule,
    MatButtonModule,
    MatInputModule,
    CapsLockNoticeComponent,
    NotifyChipComponent,
    NotifyChipValueComponent,
    MatDividerModule,
    TranslationPipeModule ],
  standalone: true,
  providers: [ provideTranslationScope("feature/change-password-dialog") ]
})
export class HeaderChangePasswordDialogComponent {
  private readonly faIconLibrary: FaIconLibrary = inject(FaIconLibrary);

  protected readonly fb: NonNullableFormBuilder = inject(NonNullableFormBuilder);

  private _authService: AuthService = inject(AuthService);

  public serverErrorMessage: WritableSignal<TranslationInfo> = signal({
    token: "",
    defaultText: "",
    scope: "feature/change-password-dialog"
  });

  // eslint-disable-next-line @unicorn/consistent-function-scoping
  public hasServerError: Signal<boolean> = computed(() => this.serverErrorMessage().token !== "");


  changePasswordForm: FormGroup<{
    currentPassword: FormControl<string>;
    newPasswordGroup: FormGroup<{
      newPassword: FormControl<string>;
      newPasswordRepeat: FormControl<string>;
    }>;
  }> = this.fb.group({
      /* eslint-disable-next-line @tseslint/unbound-method */
      currentPassword: this.fb.control("", { validators: Validators.required }),
      newPasswordGroup: this.fb.group({
        /* eslint-disable @tseslint/unbound-method */
        newPassword: this.fb.control("", { validators: [ Validators.required, Validators.minLength(8), passwordValidator() ] }),
        newPasswordRepeat: this.fb.control("", { validators: [ Validators.required, passwordMatchValidator("newPassword") ] })
        /* eslint-enable @tseslint/unbound-method */
      })
    });

  capsLockOn: WritableSignal<boolean> = signal(false);

  hideCurrentPassword: WritableSignal<boolean> = signal(true);

  hideNewPassword: WritableSignal<boolean> = signal(true);

  hideNewPasswordRepeat: WritableSignal<boolean> = signal(true);

  public isPasswordSuccessfullyChanged: WritableSignal<boolean> = signal(false);

  public get currentPassword (): FormControl<string> {
    return this.changePasswordForm.controls.currentPassword;
  }

  public get newPassword (): FormControl<string> {
    return this.changePasswordForm.controls.newPasswordGroup.controls.newPassword;
  }

  public get newPasswordRepeat (): FormControl<string> {
    return this.changePasswordForm.controls.newPasswordGroup.controls.newPasswordRepeat;
  }

  constructor () {
    this.faIconLibrary.addIcons(faCircleExclamation, faEye, faEyeSlash, faSquareArrowUp, faXmark);
    this.newPassword.valueChanges.subscribe(() => {
      this.serverErrorMessage.update((info: TranslationInfo) => ({
        ...info,
        token: "",
        defaultText: ""
      }));
      this.newPasswordRepeat.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    });
    this.currentPassword.valueChanges.subscribe(() => {
      this.serverErrorMessage.update((info: TranslationInfo) => ({
        ...info,
        token: "",
        defaultText: ""
      }));
    });
  }

  @HostListener("window:keydown", [ "$event" ])
  checkCapsLock (keyboardEvent: KeyboardEvent): void {
    this.capsLockOn.set(keyboardEvent.getModifierState("CapsLock"));
  }


  onSubmit (): void {
    this._authService.getRegisteredUser().subscribe((user: User | undefined) => {
      if (user?.email) {
        const currentPassword: string = this.changePasswordForm.controls.currentPassword.value;
        const newPassword: string = this.changePasswordForm.controls.newPasswordGroup.controls.newPassword.value;
        const newPasswordRepeat: string = this.changePasswordForm.controls.newPasswordGroup.controls.newPasswordRepeat.value;

        this._authService.changePassword(newPassword, newPasswordRepeat, user.email, currentPassword).subscribe((value: SetPasswordResponse) => {
          if (value.hasWorked)
            this.isPasswordSuccessfullyChanged.set(true);

          else {
            switch (value.passwordValidationToken) {
              case SET_PASSWORD_TOKENS.INVALID_CURRENT_PASSWORD: {
                this.serverErrorMessage.update((info: TranslationInfo) => ({
                  ...info,
                  token: "status-wrong-current-password",
                  defaultText: "Aktuelles Passwort ist falsch"
                }));

                break;
              }

              case SET_PASSWORD_TOKENS.PASSWORD_POLICY_ERROR: {
                this.serverErrorMessage.update((info: TranslationInfo) => ({
                  ...info,
                  token: "status-password-used-before",
                  defaultText: "Passwort wurde zuvor schon einmal verwendet"
                }));

                break;
              }

              default:
            }
          }
        });
      }
    });
  }
}
